/**
 * Book-builder step timing for the admin Analytics page: how long a customer
 * spends on each step and what they do there. Sent to the API
 * (POST /book-progress), which keeps it for a year linked to the book and
 * anonymously after that.
 *
 * Tracking only. Nothing here may throw, block, or change what the customer
 * sees: every entry point is wrapped, the request goes out in the background
 * and its reply is ignored — except 410, which means it was switched off on
 * the server, so we stop sending for this page load.
 *
 * Time is measured in the browser:
 *   - on-screen: the tab is in front
 *   - active:    the tab is in front and there was a tap, click, key, scroll
 *                or mouse move in the last IDLE_AFTER_MS
 * The running totals for the current visit are sent when the customer leaves
 * the step, switches tabs or closes the page. Resends are safe: the API keeps
 * the largest value.
 *
 * The Choose Photos step also times the crop window: the same on-screen and
 * active rules, counted only while it is open (useCropWindowTiming).
 * The Design Cover step times the foreword box the same way, counted only
 * while it is selected (setForewordBoxOpen), and counts how often it was
 * selected and pasted into. Never the text itself.
 *
 * Never put names, emails, titles, file names or photos in the payload.
 * No dependencies beyond React and the API base URL, so it stays tiny in the
 * customer bundle.
 */
import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/apiConfig';

export const BUILDER_STEPS = Object.freeze({
  TITLE: 'Create_New_Book',
  COVER: 'Upload_Your_Cover_Photo',
  PHOTOS: 'Upload_Images',
  DESIGN: 'Design_Your_Cover_Page',
  PREVIEW: 'Review_Your_Book',
});

/** Keys must match the API's COUNT_KEYS (utils/helpers/bookStepVisitHelpers.js). */
export const BUILDER_ACTIONS = Object.freeze({
  PHOTOS_ADDED: 'photosAdded',
  PHOTOS_REMOVED: 'photosRemoved',
  CROP_OPENED: 'cropOpened',
  CROP_SAVED: 'cropSaved',
  /** Photos the automatic crop marked "worth a look", and what happened to them. */
  CROP_FLAGGED: 'cropFlagged',
  CROP_FLAGGED_OPENED: 'cropFlaggedOpened',
  CROP_FLAGGED_SAVED: 'cropFlaggedSaved',
  /** Auto crops that fell back to centre because the face detector had not loaded. */
  CROP_NO_DETECTOR: 'cropNoDetector',
  REORDERED: 'reordered',
  FEW_PHOTOS_PROMPT_SHOWN: 'fewPhotosPromptShown',
  FEW_PHOTOS_ADDED_MORE: 'fewPhotosAddedMore',
  FEW_PHOTOS_CONTINUED: 'fewPhotosContinued',
  TOO_MANY_PHOTOS_PROMPT_SHOWN: 'tooManyPhotosPromptShown',
  PAGES_REGENERATED: 'pagesRegenerated',
  GENERATION_RETRIED: 'generationRetried',
  /** Design Cover: the foreword box was selected, and text was pasted into it. */
  FOREWORD_OPENED: 'forewordOpened',
  FOREWORD_PASTED: 'forewordPasted',
});

export const IDLE_AFTER_MS = 2 * 60 * 1000;
const INPUT_THROTTLE_MS = 1000;
/** Visits shorter than this with nothing done are noise (and React dev double-mounts). */
const MIN_SEND_MS = 1000;
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];

const STEP_VALUES = Object.values(BUILDER_STEPS);
const ACTION_VALUES = Object.values(BUILDER_ACTIONS);

let visit = null;
let switchedOff = false;
let listening = false;

const clock = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();

const tabInFront = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** Starts with a base-36 timestamp so the API's index on it stays append-only. */
function newVisitId() {
  let rand = '';
  try {
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    rand = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    rand = `${Math.random().toString(16).slice(2)}0000000000000000`.slice(0, 16);
  }
  return `${Date.now().toString(36)}-${rand}`;
}

function deviceClass() {
  const width = window.innerWidth || 0;
  if (!width) return null;
  if (width < 768) return 'phone';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/** Add the time since the last update to the visit's on-screen and active totals. */
function accrue(now) {
  if (!visit || now <= visit.accruedTo) return;
  const from = visit.accruedTo;
  if (visit.inFront) {
    const visible = now - from;
    const activeUntil = Math.min(now, visit.lastInputAt + IDLE_AFTER_MS);
    const active = activeUntil > from ? activeUntil - from : 0;
    visit.visibleMs += visible;
    visit.activeMs += active;
    if (visit.cropOpen) {
      visit.cropVisibleMs += visible;
      visit.cropActiveMs += active;
    }
    if (visit.forewordOpen) {
      visit.forewordVisibleMs += visible;
      visit.forewordActiveMs += active;
    }
  }
  visit.accruedTo = now;
}

function send() {
  if (!visit || switchedOff || typeof fetch !== 'function') return;
  const now = clock();
  accrue(now);
  const elapsedMs = Math.round(now - visit.startedAt);
  const hasCounts = Object.keys(visit.counts).length > 0;
  if (elapsedMs < MIN_SEND_MS && !hasCounts) return;
  if (!visit.bookId && visit.step !== BUILDER_STEPS.TITLE) return;

  const payload = {
    visitId: visit.id,
    step: visit.step,
    bookId: visit.bookId,
    elapsedMs,
    visibleMs: Math.round(visit.visibleMs),
    activeMs: Math.round(visit.activeMs),
    cropVisibleMs: Math.round(visit.cropVisibleMs),
    cropActiveMs: Math.round(visit.cropActiveMs),
    device: visit.device,
    counts: { ...visit.counts },
  };
  // Design Cover always says how long the foreword box was selected, even 0, so the
  // Analytics page can tell "never touched it" from a visit made before it was timed.
  if (visit.step === BUILDER_STEPS.DESIGN) {
    payload.forewordVisibleMs = Math.round(visit.forewordVisibleMs);
    payload.forewordActiveMs = Math.round(visit.forewordActiveMs);
  }
  // Nothing new since the last send (e.g. hidden, then closed).
  const fingerprint = JSON.stringify([
    payload.bookId,
    Math.floor(payload.visibleMs / 1000),
    Math.floor(payload.activeMs / 1000),
    Math.floor(payload.cropVisibleMs / 1000),
    Math.floor((payload.forewordVisibleMs || 0) / 1000),
    payload.counts,
  ]);
  if (fingerprint === visit.lastSent) return;

  let token = null;
  try {
    token = localStorage.getItem('auth_token');
  } catch (err) {
    /* storage blocked */
  }
  if (!token) return;
  visit.lastSent = fingerprint;

  const base = String(API_BASE_URL).replace(/\/?$/, '/');
  fetch(`${base}book-progress`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
    .then((res) => {
      if (res && res.status === 410) switchedOff = true;
    })
    .catch(() => {});
}

function onInput() {
  try {
    if (!visit) return;
    const now = clock();
    if (now - visit.lastInputAt < INPUT_THROTTLE_MS) return;
    accrue(now);
    visit.lastInputAt = now;
  } catch (err) {
    /* never affect the page */
  }
}

function onVisibilityChange() {
  try {
    if (!visit) return;
    const now = clock();
    accrue(now);
    visit.inFront = tabInFront();
    if (visit.inFront) {
      visit.lastInputAt = now; // coming back to the tab counts as activity
    } else {
      send();
    }
  } catch (err) {
    /* never affect the page */
  }
}

function onPageHide() {
  try {
    send();
  } catch (err) {
    /* never affect the page */
  }
}

function listen() {
  if (listening) return;
  listening = true;
  const opts = { capture: true, passive: true };
  INPUT_EVENTS.forEach((type) => document.addEventListener(type, onInput, opts));
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
}

/**
 * Begin timing a step. Ends (and sends) any visit still open.
 * @param {string} step one of BUILDER_STEPS
 * @param {string|null} bookId the active book; null only on the title step before the book exists
 * @param {object} [owner] who may end this visit (see useBuilderStepVisit)
 */
export function startStepVisit(step, bookId, owner = null) {
  try {
    if (visit) endStepVisit();
    if (!STEP_VALUES.includes(step)) return;
    const now = clock();
    visit = {
      id: newVisitId(),
      owner,
      step,
      bookId: bookId ? String(bookId) : null,
      startedAt: now,
      accruedTo: now,
      lastInputAt: now,
      inFront: tabInFront(),
      visibleMs: 0,
      activeMs: 0,
      cropOpen: false,
      cropVisibleMs: 0,
      cropActiveMs: 0,
      forewordOpen: false,
      forewordVisibleMs: 0,
      forewordActiveMs: 0,
      device: deviceClass(),
      counts: {},
      lastSent: null,
    };
    listen();
  } catch (err) {
    visit = null;
  }
}

/** Send and close the current visit — only if `owner` started it, when given. */
export function endStepVisit(owner) {
  try {
    if (!visit) return;
    if (owner !== undefined && visit.owner !== owner) return;
    send();
  } catch (err) {
    /* never affect the page */
  }
  visit = null;
}

/**
 * The step's book became known (title step) or changed. A different book
 * mid-visit closes this visit and starts a new one for the same step.
 */
export function setStepVisitBook(bookId) {
  try {
    if (!visit || !bookId) return;
    const id = String(bookId);
    if (visit.bookId === id) return;
    if (!visit.bookId) {
      visit.bookId = id;
      return;
    }
    const { step, owner } = visit;
    endStepVisit();
    startStepVisit(step, id, owner);
  } catch (err) {
    /* never affect the page */
  }
}

/** Count something the customer did on the current step. */
export function countBuilderAction(action, amount = 1) {
  try {
    if (!visit || !ACTION_VALUES.includes(action)) return;
    const n = Math.floor(Number(amount));
    if (!Number.isFinite(n) || n <= 0) return;
    visit.counts[action] = (visit.counts[action] || 0) + n;
  } catch (err) {
    /* never affect the page */
  }
}

/**
 * The crop window opened or closed on the current step. Time while it is open
 * is kept separately (cropVisibleMs / cropActiveMs) as well as in the step's
 * own totals.
 */
export function setCropWindowOpen(open) {
  try {
    if (!visit) return;
    const isOpen = Boolean(open);
    if (visit.cropOpen === isOpen) return;
    accrue(clock());
    visit.cropOpen = isOpen;
  } catch (err) {
    /* never affect the page */
  }
}

/**
 * The foreword box on Design Cover was selected or deselected. Time while it is
 * selected is kept separately (forewordVisibleMs / forewordActiveMs) as well as
 * in the step's own totals, and each time it is selected is counted. Ignored on
 * any other step.
 */
export function setForewordBoxOpen(open) {
  try {
    if (!visit || visit.step !== BUILDER_STEPS.DESIGN) return;
    const isOpen = Boolean(open);
    if (visit.forewordOpen === isOpen) return;
    accrue(clock());
    visit.forewordOpen = isOpen;
    if (isOpen) countBuilderAction(BUILDER_ACTIONS.FOREWORD_OPENED);
  } catch (err) {
    /* never affect the page */
  }
}

/** Time the crop window on the current step while `open` is true. */
export function useCropWindowTiming(open) {
  useEffect(() => {
    setCropWindowOpen(open);
    return () => setCropWindowOpen(false);
  }, [open]);
}

/** Time this screen as `step` while it is mounted. */
export function useBuilderStepVisit(step, bookId) {
  const owner = useRef({});
  useEffect(() => {
    const me = owner.current;
    startStepVisit(step, bookId, me);
    return () => endStepVisit(me);
    // bookId changes are handled below without restarting the visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  useEffect(() => {
    setStepVisitBook(bookId);
  }, [bookId]);
}

/** Test hook: forget the current visit and the off switch. */
export function resetStepTimingForTests() {
  visit = null;
  switchedOff = false;
}
